import React, { useEffect, useMemo, useState } from 'react';
import { X, ArrowLeft } from 'lucide-react';
import { WS, type SkillItem } from './workspaceData.ts';

export interface SkillTemplateField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'date' | 'number';
  required?: boolean;
  placeholder?: string;
  options?: string[];
}
export interface SkillTemplate {
  id: string;
  name: string;
  description: string;
  tags?: string[];
  output_format?: string;
  fields: SkillTemplateField[];
  prompt: string;
  preview_svg?: string;
}

const previewSrc = (svg?: string) => (svg ? `data:image/svg+xml;utf8,${encodeURIComponent(svg)}` : '');

/** Fill {{key}} placeholders; empty optional values read as "not specified". */
export function renderTemplate(template: SkillTemplate, values: Record<string, string>): string {
  return template.prompt.replace(/{{\s*(\w+)\s*}}/g, (_m, key) => (values[key] || '').trim() || 'not specified');
}

const _cache: Record<string, SkillTemplate[]> = {};

async function fetchTemplates(skillId: string): Promise<SkillTemplate[]> {
  if (_cache[skillId]) return _cache[skillId];
  const token = localStorage.getItem('authToken');
  const res = await fetch(
    `${(process.env.REACT_APP_API_URL as string) || ''}/api/skills/catalogue/${encodeURIComponent(skillId)}/templates`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  const data = await res.json();
  _cache[skillId] = data.templates || [];
  return _cache[skillId];
}

interface Props {
  skill: SkillItem;
  onClose: () => void;
  /** Receives the filled-in prompt to add to the agent's instructions. */
  onApply: (text: string, template: SkillTemplate) => void;
}

export default function SkillTemplatesModal({ skill, onClose, onApply }: Props) {
  const [templates, setTemplates] = useState<SkillTemplate[] | null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    fetchTemplates(skill.id)
      .then(t => { if (!cancelled) { setTemplates(t); setSelectedId(t.length === 1 ? t[0].id : ''); } })
      .catch(() => { if (!cancelled) setTemplates([]); });
    return () => { cancelled = true; };
  }, [skill.id]);

  const template = useMemo(() => templates?.find(t => t.id === selectedId) || null, [templates, selectedId]);
  useEffect(() => { setValues({}); }, [selectedId]);

  const missing = template ? template.fields.filter(f => f.required && !(values[f.key] || '').trim()) : [];
  const preview = template ? renderTemplate(template, values) : '';

  const label: React.CSSProperties = { display: 'block', fontSize: 11.5, fontWeight: 600, color: WS.ink3, margin: '10px 0 4px' };
  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', background: 'var(--lyra-color-surface)', border: `1px solid ${WS.border}`,
    borderRadius: 9, padding: '8px 12px', fontSize: 13, color: WS.ink, fontFamily: 'inherit',
  };

  return (
    <div
      role="dialog" aria-modal="true" aria-label={`${skill.name} templates`}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,.45)', display: 'grid', placeItems: 'center', padding: 16 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--lyra-color-surface)', borderRadius: 16, width: 'min(980px, 100%)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 70px rgba(15,23,42,.25)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${WS.border}` }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: WS.ink3 }}>TEMPLATES</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: WS.ink }}>{skill.name}</div>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} style={{ border: 0, background: 'transparent', cursor: 'pointer', color: WS.ink3 }}><X size={18} /></button>
        </div>

        {templates === null && <div style={{ padding: 28, color: WS.ink3, fontSize: 13 }}>Loading templates…</div>}
        {templates?.length === 0 && <div style={{ padding: 28, color: WS.ink3, fontSize: 13 }}>No templates for this skill yet.</div>}

        {!!templates?.length && !template && (
          <div style={{ padding: 20, overflowY: 'auto' }}>
            <div style={{ fontSize: 13, color: WS.ink3, marginBottom: 14 }}>Pick a starting point — you can adjust the details on the next step.</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 16 }}>
              {templates.map(t => (
                <button
                  key={t.id} type="button" onClick={() => setSelectedId(t.id)}
                  style={{ textAlign: 'left', border: `1px solid ${WS.border}`, borderRadius: 14, background: 'var(--lyra-color-surface)', padding: 0, cursor: 'pointer', overflow: 'hidden', transition: 'box-shadow .15s, border-color .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 10px 28px rgba(15,23,42,.12)'; e.currentTarget.style.borderColor = WS.accent; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = WS.border; }}
                >
                  <div style={{ background: 'var(--lyra-color-surface-2)', aspectRatio: '16 / 10', display: 'grid', placeItems: 'center' }}>
                    {t.preview_svg
                      ? <img src={previewSrc(t.preview_svg)} alt={`${t.name} preview`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      : <span style={{ color: WS.ink3, fontSize: 12 }}>No preview</span>}
                  </div>
                  <div style={{ padding: '10px 14px 14px' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: WS.ink }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: WS.ink3, marginTop: 3, lineHeight: 1.4 }}>{t.description}</div>
                    {t.output_format && <span style={{ display: 'inline-block', marginTop: 8, fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', color: WS.accent, background: WS.accentBg, borderRadius: 999, padding: '2px 8px' }}>.{t.output_format.toUpperCase()}</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {!!templates?.length && template && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 300px) 1fr', minHeight: 0, flex: 1, overflow: 'hidden' }}>
            <div style={{ borderRight: `1px solid ${WS.border}`, overflowY: 'auto', padding: 16 }}>
              {templates.length > 1 && (
                <button type="button" onClick={() => setSelectedId('')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: 0, background: 'transparent', color: WS.ink2, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 12 }}>
                  <ArrowLeft size={14} /> All templates
                </button>
              )}
              {template.preview_svg && <img src={previewSrc(template.preview_svg)} alt={`${template.name} preview`} style={{ width: '100%', borderRadius: 10, border: `1px solid ${WS.border}`, display: 'block' }} />}
              <div style={{ fontSize: 15, fontWeight: 700, color: WS.ink, marginTop: 12 }}>{template.name}</div>
              <div style={{ fontSize: 12.5, color: WS.ink3, marginTop: 4, lineHeight: 1.45 }}>{template.description}</div>
            </div>

            <div style={{ overflowY: 'auto', padding: '6px 20px 16px' }}>
              {template.fields.map(f => (
                <div key={f.key}>
                  <label style={label} htmlFor={`tpl-${f.key}`}>{f.label}{f.required ? ' *' : ''}</label>
                  {f.type === 'textarea' ? (
                    <textarea id={`tpl-${f.key}`} rows={3} style={{ ...input, resize: 'vertical' }} placeholder={f.placeholder} value={values[f.key] || ''} onChange={e => setValues({ ...values, [f.key]: e.target.value })} />
                  ) : f.type === 'select' ? (
                    <select id={`tpl-${f.key}`} style={input} value={values[f.key] || ''} onChange={e => setValues({ ...values, [f.key]: e.target.value })}>
                      <option value="">Choose…</option>
                      {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input id={`tpl-${f.key}`} type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'} style={input} placeholder={f.placeholder} value={values[f.key] || ''} onChange={e => setValues({ ...values, [f.key]: e.target.value })} />
                  )}
                </div>
              ))}
              <div style={{ ...label, marginTop: 16 }}>Instruction preview{template.output_format ? ` · output: .${template.output_format}` : ''}</div>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 12.5, lineHeight: 1.5, background: WS.accentBg, borderRadius: 10, padding: 12, color: WS.ink, margin: 0 }}>{preview}</pre>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, padding: '12px 20px', borderTop: `1px solid ${WS.border}` }}>
          {template && missing.length > 0 && <span style={{ fontSize: 12, color: WS.ink3, marginRight: 'auto' }}>Fill in: {missing.map(f => f.label).join(', ')}</span>}
          <button type="button" onClick={onClose} style={{ ...input, width: 'auto', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
          <button
            type="button" disabled={!template || missing.length > 0}
            onClick={() => template && onApply(preview, template)}
            style={{ border: 0, borderRadius: 9, padding: '9px 16px', fontSize: 13, fontWeight: 600, color: 'var(--app-on-accent)', background: WS.accent, cursor: !template || missing.length ? 'not-allowed' : 'pointer', opacity: !template || missing.length ? 0.5 : 1 }}
          >
            Add to agent instructions
          </button>
        </div>
      </div>
    </div>
  );
}
